// src/components/GenerationQueue.jsx

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Check,
  AlertCircle,
  XCircle,
  Clock,
  Volume2,
  VolumeX,
  ChevronDown,
  Play,
  Film,
  GraduationCap,
  PenLine,
  Sparkles,
} from 'lucide-react';
import { trackFunnelEvent } from '../utils/ev';
import { apiService } from '../apiService';
import { GENERIC_ERROR_MESSAGE } from '../utils/constants';
import { useT } from '../i18n/useT';
// Moved to utils/sfx.js so the session pages can play the same two sounds — this panel is
// usually minimised, and these mark events that happen whether or not it is on screen.
import { playCompletionChimeOnce, playStartSound } from '../utils/sfx';

// Animations (injected once)
const STYLE_ID = 'queue-anims';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes q-shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes q-fade-in {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes q-pulse-ring {
      0% { transform: scale(1); opacity: 0.6; }
      50% { transform: scale(1.15); opacity: 0.3; }
      100% { transform: scale(1); opacity: 0.6; }
    }
    @keyframes q-celebrate {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
  `;
  document.head.appendChild(s);
}

const CHIME_ENABLED_KEY = 'magi_queue_chime_enabled';
const FIRST_TIME_KEY = 'magi_queue_first_time_shown';

// Project type styling
const PROJECT_COLORS = {
  hw: { dot: 'bg-indigo-500', light: 'bg-indigo-50/80', bar: '#6366F1', badge: 'bg-indigo-100 text-indigo-600' },
  lecture: { dot: 'bg-purple-500', light: 'bg-purple-50/80', bar: '#8B5CF6', badge: 'bg-purple-100 text-purple-600' },
  self_study: { dot: 'bg-teal-500', light: 'bg-teal-50/80', bar: '#14B8A6', badge: 'bg-teal-100 text-teal-600' },
  video: { dot: 'bg-rose-500', light: 'bg-rose-50/80', bar: '#F43F5E', badge: 'bg-rose-100 text-rose-600' },
};
const PROJECT_LABELS = {
  hw: { label: 'Homework', Icon: PenLine },
  lecture: { label: 'Lecture', Icon: GraduationCap },
  self_study: { label: 'Project', Icon: Sparkles },
  video: { label: 'Video', Icon: Play },
};

// Progress — single source of truth for generation time estimates.
//
// 60s: the target a lesson is SUPPOSED to take, set deliberately rather than fitted to the
// current distribution. Measured 2026-09-14 over the last 400 prod `video_costs` rows (non-
// reel, almost all mode_fast_37): total_pipeline p50 59s / p75 79s / p90 113s, final_ready
// p50 69s / p75 88s / p90 114s. So the median lands about here and slower ones will run past
// it into "Almost ready…" — which is the honest thing for the dial to say when a generation
// is taking longer than it should, and a reason to make it faster rather than to pad the
// estimate. An earlier pass set this to 100 (≈p85) to keep the overrun rare; that hid the
// slow tail instead of reporting it.
// 120, RAISED FROM 60 ON 2026-10-27. The 60 above was measured when a scene had far more of
// a render container to itself; measured again under real concurrency, a scene's render alone
// runs 26-92s depending on how many share its machine, and the wait for a container is
// another 0-60s on top with no pattern we have been able to find. A 60s line therefore ran
// out before the median video was ready and sat on "Almost ready…" for most of its life,
// which tells the person nothing. 120 covers the common case honestly; the slow tail still
// overruns, and that is still the right thing for it to do.
export const COUNTDOWN_TOTAL_VIDEO = 120;
// Projects are N videos generated together; no per-project measurement behind this one.
export const COUNTDOWN_TOTAL_OTHER = 195;

const ProgressSection = ({ job }) => {
  const t = useT();
  const getStart = () => job.processingStartedAt ? new Date(job.processingStartedAt).getTime() : Date.now();
  const [elapsed, setElapsed] = useState(() =>
    job.state === 'processing' && job.processingStartedAt
      ? Math.floor((Date.now() - new Date(job.processingStartedAt).getTime()) / 1000)
      : 0
  );
  const startRef = useRef(job.state === 'processing' ? getStart() : null);
  const failedScenes = (job.scenes || []).filter(s => s.status === 'failed').length;
  const effectiveType = job.metadata?.projectType || job.type;
  // The backend's per-job estimate wins when it sent one — it knows which renderer the job's
  // prompt package uses, and a ManimCE lesson is about a minute against the 120 below. The
  // constants stay the default for every job that carries no estimate.
  const baseCountdown = job.estimatedSeconds
    || (effectiveType === 'video' ? COUNTDOWN_TOTAL_VIDEO : COUNTDOWN_TOTAL_OTHER);
  const countdownTotal = baseCountdown + (failedScenes * 60);

  useEffect(() => {
    if (job.state !== 'processing') return;
    if (!startRef.current) startRef.current = getStart();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(tick);
  }, [job.state]);

  const done = job.state === 'completed';
  const remaining = Math.max(0, countdownTotal - elapsed);
  const pct = done ? 100 : Math.min(98, (elapsed / countdownTotal) * 100);

  const fmt = (s) => {
    if (s <= 0) return t('queue_almost_ready');
    if (s < 60) return t('queue_seconds_left', { n: s });
    return t('queue_minutes_left', { m: Math.floor(s / 60), s: s % 60 });
  };

  return (
    <div className="mt-3">
      <div className="relative w-full h-2 rounded-full overflow-hidden bg-gray-100">
        <div
          className="h-full rounded-full relative"
          style={{
            width: `${pct}%`,
            background: done ? '#10B981' : 'linear-gradient(90deg, #6366F1, #8B5CF6)',
            transition: done ? 'width 0.5s ease-out' : 'width 1s linear',
          }}
        >
          {!done && (
            <div
              className="absolute inset-0 w-full"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'q-shimmer 1.5s infinite',
              }}
            />
          )}
        </div>
      </div>
      <p className="text-[11px] mt-2 text-gray-400 font-medium">
        {done ? t('queue_ready') : job.state === 'queued' ? t('queue_in_queue') : fmt(remaining)}
      </p>
    </div>
  );
};

// Job Card
/* CANCELLING CAN FAIL, AND NOW IT SAYS SO. `cancelJob` used to swallow every refusal and
   drop the row anyway, so these buttons needed no error handling — the lie was upstream.
   With the hook re-throwing, an unhandled rejection here would be the new silence, so the
   card keeps its row and shows the reason. */
const useCancel = (onCancel) => {
  const [cancelErr, setCancelErr] = useState(null);
  const run = (id) => {
    setCancelErr(null);
    Promise.resolve(onCancel(id)).catch((e) =>
      setCancelErr(e?.message || 'could not cancel that'));
  };
  return [cancelErr, run];
};

const JobCard = ({ job, isRecent, onView, onCancel, onRemove }) => {
  const t = useT();
  const [cancelErr, cancel] = useCancel(onCancel);
  const proc = job.state === 'processing';
  const done = job.state === 'completed';
  const fail = job.state === 'failed';
  const wait = job.state === 'queued';

  return (
    <div
      onClick={() => done && onView(job)}
      className={`relative rounded-2xl p-4 transition-all duration-300 ${
        done
          ? 'bg-emerald-50/80 cursor-pointer hover:bg-emerald-50'
          : proc
          ? 'bg-indigo-50/50'
          : fail
          ? 'bg-amber-50/50'
          : 'bg-gray-50/50'
      }`}
      style={{
        animation: isRecent ? 'q-celebrate 0.6s ease-out' : 'q-fade-in 0.3s ease-out',
      }}
    >
      <div className="flex items-start gap-3">
        {/* Status indicator */}
        <div className="flex-shrink-0 mt-0.5">
          {proc ? (
            <div className="relative w-5 h-5 flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-full bg-indigo-400/30"
                style={{ animation: 'q-pulse-ring 2s ease-in-out infinite' }}
              />
              <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
            </div>
          ) : done ? (
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
          ) : fail ? (
            <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
            </div>
          ) : wait ? (
            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
              <Clock className="w-3 h-3 text-gray-500" />
            </div>
          ) : (
            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
              <XCircle className="w-3.5 h-3.5 text-gray-400" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-[13px] font-semibold text-gray-900 truncate leading-tight">
            {job.title}
          </h4>

          {(proc || isRecent) && <ProgressSection job={job} />}

          {done && !isRecent && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Play className="w-3 h-3 text-emerald-600" fill="currentColor" />
              <span className="text-xs font-medium text-emerald-600">{t('queue_watch_now')}</span>
            </div>
          )}
          {fail && (
            <p className="text-[11px] text-amber-600 mt-1.5 font-medium">
              Generation failed. Credit refunded.
            </p>
          )}
          {wait && !cancelErr && (
            <p className="text-[11px] text-gray-400 mt-1.5">{t('queue_waiting')}</p>
          )}
          {cancelErr && (
            <p className="text-[11px] text-amber-600 mt-1.5 font-medium">
              Still running — {cancelErr}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          {wait && (
            <button
              onClick={e => { e.stopPropagation(); cancel(job.id); }}
              className="p-1.5 rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all"
              title="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {(done || fail || job.state === 'cancelled') && (
            <button
              onClick={e => { e.stopPropagation(); onRemove(job.id); }}
              className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all"
              title="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Project Card (groups multiple sub-jobs into one entry)
const ProjectCard = ({ job, isRecent, onView, onCancel, onRemove }) => {
  const t = useT();
  const [cancelErr, cancel] = useCancel(onCancel);
  const projectType = job.metadata?.projectType || 'video';
  const colors = PROJECT_COLORS[projectType] || PROJECT_COLORS.video;
  const labelInfo = PROJECT_LABELS[projectType] || PROJECT_LABELS.video;

  const done = job.state === 'completed';
  const fail = job.state === 'failed';
  const active = job.state === 'processing' || job.state === 'queued';

  const total = job.totalCount || job.metadata?.subJobIds?.length || 1;

  return (
    <div
      onClick={() => done && onView(job)}
      className={`relative rounded-2xl p-4 transition-all duration-300 ${
        done ? `${colors.light} cursor-pointer` : fail ? 'bg-amber-50/50' : 'bg-gray-50/50'
      }`}
      style={{ animation: isRecent ? 'q-celebrate 0.6s ease-out' : 'q-fade-in 0.3s ease-out' }}
    >
      <div className="flex items-start gap-3">
        {/* Colored type indicator */}
        <div className="flex-shrink-0 mt-0.5">
          {active ? (
            <div className="relative w-5 h-5 flex items-center justify-center">
              <div
                className={`absolute inset-0 rounded-full ${colors.dot} opacity-30`}
                style={{ animation: 'q-pulse-ring 2s ease-in-out infinite' }}
              />
              <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
            </div>
          ) : done ? (
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
          ) : fail ? (
            <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
            </div>
          ) : (
            <div className={`w-5 h-5 rounded-full ${colors.dot} flex items-center justify-center`}>
              <div className="w-2.5 h-2.5 rounded-full bg-white/60" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-[13px] font-semibold text-gray-900 truncate leading-tight">
              {job.title}
            </h4>
            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${colors.badge}`}>
              {labelInfo.label}
            </span>
          </div>

          {(active || isRecent) && <ProgressSection job={job} />}

          {done && !isRecent && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Play className="w-3 h-3 text-emerald-600" fill="currentColor" />
              <span className="text-xs font-medium text-emerald-600">
                {total === 1 ? t('queue_watch_now') : `All ${total} videos ready`}
              </span>
            </div>
          )}
          {fail && (
            <p className="text-[11px] text-amber-600 mt-1.5 font-medium">
              Generation failed. Credit refunded.
            </p>
          )}
          {cancelErr && (
            <p className="text-[11px] text-amber-600 mt-1.5 font-medium">
              Still running — {cancelErr}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          {active && (
            <button
              onClick={e => { e.stopPropagation(); cancel(job.id); }}
              className="p-1.5 rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all"
              title="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {(done || fail) && (
            <button
              onClick={e => { e.stopPropagation(); onRemove(job.id); }}
              className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all"
              title="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const GenerationQueue = ({ queue, isMinimized, setIsMinimized, removeJob, cancelJob, clearCompleted, forceMinimized = false }) => {
  const navigate = useNavigate();
  const t = useT();

  const [chimeEnabled, setChimeEnabled] = useState(() => {
    const v = localStorage.getItem(CHIME_ENABLED_KEY);
    return v === null ? true : v === 'true';
  });
  const toggleChime = useCallback(() => {
    setChimeEnabled(p => { const n = !p; localStorage.setItem(CHIME_ENABLED_KEY, String(n)); return n; });
  }, []);

  const [showFirstTimeTooltip, setShowFirstTimeTooltip] = useState(false);
  const firstTimeRef = useRef(localStorage.getItem(FIRST_TIME_KEY) === 'true');
  const [recentlyCompleted, setRecentlyCompleted] = useState(new Set());
  const prevStates = useRef({});
  const prevLen = useRef(queue.length);

  // New job added
  useEffect(() => {
    if (queue.length > prevLen.current) {
      // Silent jobs (e.g. session follow-ups) stay in the queue but must not
      // auto-expand a minimized panel.
      const added = queue.slice(prevLen.current);
      const hasVisibleNew = added.some(j => !j.silent);
      if (hasVisibleNew && !forceMinimized) setIsMinimized(false);
      if (hasVisibleNew && chimeEnabled) playStartSound();
      if (hasVisibleNew && !firstTimeRef.current) {
        firstTimeRef.current = true;
        localStorage.setItem(FIRST_TIME_KEY, 'true');
        setShowFirstTimeTooltip(true);
        setTimeout(() => setShowFirstTimeTooltip(false), 10000);
      }
    }
    prevLen.current = queue.length;
  }, [queue.length, queue, forceMinimized, chimeEnabled, setIsMinimized]);

  // Completion detection
  useEffect(() => {
    queue.forEach(job => {
      const prev = prevStates.current[job.id];
      if (job.state === 'completed' && prev && prev !== 'completed') {
        // Deduped: the job's tile and its session row watch the same completion.
        if (chimeEnabled) playCompletionChimeOnce(job.id);
        if (!recentlyCompleted.has(job.id)) {
          setRecentlyCompleted(s => new Set([...s, job.id]));
          setTimeout(() => setRecentlyCompleted(s => { const n = new Set(s); n.delete(job.id); return n; }), 3000);
        }
      }
      prevStates.current[job.id] = job.state;
    });
  }, [queue]);

  const handleViewVideo = (job) => {
    if (job.state !== 'completed') return;
    trackFunnelEvent('watch');

    if (job.type === 'project') {
      const sessionId = job.metadata?.sessionId;
      if (!sessionId) return;
      const collection = job.metadata?.collection;
      const projectType = job.metadata?.projectType;
      if (collection === 'homework') {
        navigate(`/homework/session/${sessionId}`);
      } else if (projectType === 'video') {
        // A single-video project opens the standalone player, not a session.
        const vid = job.videoId || job.id;
        navigate(`/video/${vid}`, { state: { videoId: vid, title: job.title || 'Generated Video', previousPage: 'queue', jobId: job.id } });
      } else {
        navigate(`/workspace/session/${sessionId}`);
      }
      return;
    }

    apiService.trackClick(job.id).catch(() => {});
    if (job.type === 'course_generation') {
      navigate(`/course/${job.id}`, { state: { courseId: job.id, fromQueue: true } });
    } else {
      const vid = job.videoId || job.id;
      navigate(`/video/${vid}`, { state: { videoId: vid, title: job.title || 'Generated Video', description: `Generated ${job.type} video`, previousPage: 'queue', jobId: job.id } });
    }
  };

  const active = queue.filter(j => j.state === 'processing' || j.state === 'queued');
  const isLive = active.length > 0;
  /* A SILENT JOB IS TRACKED, NOT DRAWN. The flag has been on every job record since this
     hook was written and nothing ever read it here, so there was no way to put work into the
     queue without also putting a card on the screen. A test set needs exactly that: its ten
     cells are real jobs and must be polled, counted and cancellable like any other, but ten
     identical cards is the queue saying nothing ten times. The set adds one `project` card
     that aggregates them — see PromptTestSets — and the cells go in silent behind it.
     Only the LIST is filtered: `active` and `completedCount` below still count them, because
     they are genuinely work in flight and the pill is about how busy the account is. */
  const reversed = useMemo(() => [...queue].filter(j => !j.silent).reverse(), [queue]);
  const completedCount = queue.filter(j => j.state === 'completed' || j.state === 'failed' || j.state === 'cancelled').length;

  const showPill = isMinimized && !(forceMinimized && queue.length === 0);

  return (
    <>
      {/* Minimized Pill */}
      {showPill && (
        <button
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-[4.5rem] md:bottom-5 right-5 sm:right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-2xl transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
          style={{
            backgroundColor: isLive ? '#4F46E5' : '#FFFFFF',
            border: isLive ? 'none' : '1px solid #E5E7EB',
            boxShadow: isLive
              ? '0 8px 24px rgba(79,70,229,0.3), 0 2px 8px rgba(0,0,0,0.1)'
              : '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
          }}
        >
          {isLive && (
            <div className="relative w-2 h-2">
              <div className="absolute inset-0 rounded-full bg-white/60" style={{ animation: 'q-pulse-ring 2s infinite' }} />
              <div className="w-2 h-2 rounded-full bg-white" />
            </div>
          )}
          <Film className={`w-4 h-4 ${isLive ? 'text-white' : 'text-gray-500'}`} />
          <span className={`text-sm font-semibold ${isLive ? 'text-white' : 'text-gray-700'}`}>
            {isLive ? t('queue_generating', { n: active.length }) : `${queue.length}`}
          </span>
        </button>
      )}

      {/* Expanded Card — always mounted, hidden via CSS when minimized */}
      <div
        className="fixed bottom-[4.5rem] md:bottom-5 right-5 sm:right-6 z-50 rounded-3xl overflow-hidden flex flex-col"
        style={{
          display: isMinimized ? 'none' : undefined,
          width: 'min(360px, calc(100vw - 2rem))',
          maxHeight: 'min(520px, calc(100vh - 6rem))',
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)',
          animation: isMinimized ? 'none' : 'q-fade-in 0.2s ease-out',
        }}
      >
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <Film className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('queue_title')}
          </h3>
          {isLive && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-600">
              {t('queue_active', { n: active.length })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleChime}
            className="p-2 rounded-xl transition-all hover:bg-gray-100"
            title={chimeEnabled ? 'Mute sounds' : 'Unmute sounds'}
          >
            {chimeEnabled
              ? <Volume2 className="w-4 h-4 text-gray-400" />
              : <VolumeX className="w-4 h-4 text-gray-300" />
            }
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-2 rounded-xl transition-all hover:bg-gray-100"
            title="Minimize"
          >
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* First time tooltip */}
      {showFirstTimeTooltip && (
        <div className="mx-4 mt-3 p-3.5 rounded-xl bg-indigo-50 border border-indigo-100 relative">
          <button
            onClick={() => setShowFirstTimeTooltip(false)}
            className="absolute top-2.5 right-2.5 p-0.5 rounded text-indigo-300 hover:text-indigo-500 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
          <p className="text-xs font-medium text-indigo-900 pr-4">{t('queue_first_time')}</p>
          <p className="text-[11px] text-indigo-600/70 mt-0.5">{t('queue_first_time_sub')}</p>
        </div>
      )}

      {/* Jobs */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {queue.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="w-10 h-10 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Film className="w-5 h-5 text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">{t('queue_empty')}</p>
            <p className="text-xs text-gray-300 mt-1">{t('queue_empty_sub')}</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {reversed.map((job) => (
              job.type === 'project' ? (
                <ProjectCard
                  key={job.uid || job.id}
                  job={job}
                  isRecent={recentlyCompleted.has(job.id)}
                  onView={handleViewVideo}
                  onCancel={cancelJob}
                  onRemove={removeJob}
                />
              ) : (
                <JobCard
                  key={job.uid || job.id}
                  job={job}
                  isRecent={recentlyCompleted.has(job.id)}
                  onView={handleViewVideo}
                  onCancel={cancelJob}
                  onRemove={removeJob}
                />
              )
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {completedCount > 0 && (
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <button
            onClick={clearCompleted}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
          >
            {t('queue_clear')}
          </button>
          <span className="text-[10px] text-gray-300">{t('queue_total', { n: queue.length })}</span>
        </div>
      )}
    </div>
    </>
  );
};

export default GenerationQueue;
